/**
 * api.js — thin wrapper over the tracking-api PWA endpoints.
 *
 * All requests are same-origin: the PWA is served from /pwa/ on the same host
 * as /api/portal/*. No CORS, no token leakage to third-party domains.
 *
 * Auth is the stateless HMAC session from /api/portal/pharmacy-login, sent
 * as Bearer on every subsequent call. Sessions expire in 12h server-side;
 * on 401 the caller should redirect to the login screen.
 */
import { getUser, clearUser } from './db'

const BASE = '/api/portal'

async function call(path, { method = 'POST', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' }
  if (auth) {
    const u = await getUser()
    if (!u?.token) throw new ApiError('Not signed in', 'NO_SESSION', 401)
    headers.Authorization = `Bearer ${u.token}`
  }
  let resp
  try {
    resp = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch (err) {
    throw new ApiError('Network unreachable', 'NETWORK', 0, err)
  }
  let data = {}
  try { data = await resp.json() } catch {/* empty body */}
  if (!resp.ok || data?.success === false) {
    const code = data?.code || `HTTP_${resp.status}`
    if (resp.status === 401) {
      // Session is no good — clear it so the next render bounces to login.
      await clearUser()
    }
    throw new ApiError(data?.error || `HTTP ${resp.status}`, code, resp.status)
  }
  return data
}

export class ApiError extends Error {
  constructor(message, code, status, cause) {
    super(message)
    this.code = code
    this.status = status
    this.cause = cause
  }
}

// ── PWA-facing endpoints ────────────────────────────────────────────────────

export function login({ pharmacyName, username, password }) {
  return call('/pharmacy-login', { body: { pharmacyName, username, password }, auth: false })
}

export function fetchDrugs() {
  return call('/drugs-full', { body: {} })
}

export function submitSale(payload) {
  return call('/pos-submit', { body: payload })
}
